import React, {useState} from 'react';
import {Button, Spinner} from 'react-bootstrap';
import classNames from 'classnames';
import css from './ButtonV2.module.scss';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {IconDefinition as IconDefinitionBrands} from '@fortawesome/free-brands-svg-icons';
import {IconDefinition as IconDefinitionSolid} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

type ButtonV2Props = {
  className?: string;
  children: React.ReactNode;
  onClick?: () => void | null;
  onClickAsync?: () => Promise<void>;
  onLoaderClick?: () => Promise<void>;
  disabled?: boolean;
  withClickLoader?: boolean;
  type?: 'primary' | 'primary-outline' | 'secondary' | 'twitch' | 'warning';
  size?: 'small' | 'medium';
  href?: string;
  target?: string;
  prefixIcon?: IconDefinition | IconDefinitionBrands | IconDefinitionSolid;
  prefixIconComponent?: React.ReactNode;

  copy?: {
    textToCopy: string;
    notificationText?: string;
  };

  rel?: string;
};

const ButtonSpinner = () => (
  <div className={css.buttonSpinner}>
    <Spinner size="sm" animation="border" />
  </div>
);

export const ButtonV2 = (props: ButtonV2Props) => {
  const {
    children,
    className,
    onLoaderClick,
    onClickAsync,
    disabled,
    type = 'primary',
    size = 'medium',
    withClickLoader,
    prefixIcon,
    prefixIconComponent,
    copy,
    ...restProps
  } = props;

  let onClick = props.onClick || props.onClickAsync;

  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const onClickHandlerCopy = async () => {
    if (copy) {
      if (copy?.notificationText) {
        setIsCopied(true);
      }

      try {
        await navigator.clipboard.writeText(copy.textToCopy);
        await onClick?.();
      } catch (e) {
        console.error(e);
      }

      await new Promise((resolve) => setTimeout(resolve, 1500));
      setIsCopied(false);
    }
  };

  const onClickHandler = async () => {
    if (onLoaderClick) {
      setIsLoading(true);
      await onLoaderClick?.();
      setIsLoading(false);
    } else if (withClickLoader) {
      setIsLoading(true);
      await onClick?.();
      setIsLoading(false);
    } else {
      onClick?.();
    }
  };

  return (
    <Button
      {...restProps}
      className={classNames(
        css.button,
        {
          [css.buttomSmall]: size === 'small',
          [css.buttonMedium]: size === 'medium',

          [css.buttonPrimary]: type === 'primary',
          [css.buttonPrimaryOutline]: type === 'primary-outline',
          [css.buttonSecondary]: type === 'secondary',
          [css.buttonTwitch]: type === 'twitch',
          [css.buttonWarning]: type === 'warning',
          [css.disabled]: disabled,
        },
        className
      )}
      disabled={disabled}
      onClick={copy ? onClickHandlerCopy : onClickHandler}
      variant={type}
    >
      {withClickLoader && isLoading && <ButtonSpinner />}

      {prefixIcon && (
        <FontAwesomeIcon
          size="lg"
          icon={prefixIcon as IconDefinition}
          className={css.prefixIcon}
        />
      )}

      {!prefixIcon &&
        !!prefixIconComponent &&
        React.cloneElement(prefixIconComponent as React.ReactElement, {
          className: css.prefixIcon,
        })}

      {!isCopied && children}

      {isCopied && copy?.notificationText}
    </Button>
  );
};
